import { Button, Container, CopyToClipboard, DescriptionSkeleton, IconButton, Skeleton } from "../../../shared/ui";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { classNames } from "../../../shared/lib/classNames";
import { VacancyApplyForm, useGetVacancyQuery, VacancyTabs } from "../../../entities/Vacancy";
import { advantages } from "../consts";
import { generateMediaQuery } from "../../../shared/lib/helpers";
import { MediaQueryProperty, MediaSizeEnum } from "../../../shared/constants/media";
import { useMediaQuery } from "../../../shared/hooks/useMediaQuery";

import styles from "./VacancyMain.module.scss";
import "../../../shared/styles/quill.snow.css";
import { Navigate } from "react-router-dom";
import { RoutesEnum } from "../../../shared/constants/routes";

interface VacancyMainProps {
    id: string;
}

const advantagesList = advantages.map(({ icon, description }, index) => (
    <li className={styles.footerItem} key={index}>
        {icon}
        {description}
    </li>
));

export const VacancyMain = ({ id }: VacancyMainProps) => {
    const isMobile = useMediaQuery(generateMediaQuery(MediaQueryProperty.MaxWidth, MediaSizeEnum.sm));
    const isTablet = useMediaQuery(generateMediaQuery(MediaQueryProperty.MaxWidth, MediaSizeEnum.md));

    const { data: vacancy, isLoading: isLoadingVacancy } = useGetVacancyQuery(id ?? skipToken);

    const handleButtonClick = () => {
        document.querySelector("#form")?.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
    };

    if (!vacancy && !isLoadingVacancy) return <Navigate to={RoutesEnum.NotFound} />;

    return (
        <Container marginSize={!isMobile ? "standard" : undefined}>
            <div className={styles.sections}>
                <div className={styles.main}>
                    <div>
                        {vacancy?.name && <h1 className={styles.title}>{vacancy.name}</h1>}

                        <VacancyTabs vacancy={vacancy} iconSize="medium" className={styles.tabs} />

                        {isLoadingVacancy ? (
                            <Skeleton className={styles.tag} />
                        ) : (
                            !!vacancy?.tags?.length && (
                                <div className={styles.tags}>
                                    {vacancy.tags.map(({ name }) => (
                                        <span key={name} className={styles.tag}>
                                            {name}
                                        </span>
                                    ))}
                                </div>
                            )
                        )}

                        {isTablet && (
                            <div className={styles.buttonList}>
                                <Button
                                    className={styles.respond}
                                    isDisabled={isLoadingVacancy}
                                    as="button"
                                    theme="blue"
                                    onClick={handleButtonClick}
                                    size="s"
                                >
                                    Откликнуться
                                </Button>

                                <div className={styles.buttons}>
                                    <CopyToClipboard copiedValue={window.location.href}>
                                        <IconButton className={styles.copy} icon="copy" />
                                    </CopyToClipboard>
                                </div>
                            </div>
                        )}

                        {isLoadingVacancy ? (
                            <DescriptionSkeleton items={30} />
                        ) : (
                            <>
                                {vacancy?.sample?.description?.map((item, index) => (
                                    <p className={styles.paragraph} key={index}>
                                        {item}
                                    </p>
                                ))}

                                {!!vacancy?.description && (
                                    <div
                                        id="editor"
                                        dangerouslySetInnerHTML={{ __html: vacancy.description }}
                                        className={classNames({ cls: "ql-editor", additional: [styles.vacancy] })}
                                    />
                                )}
                            </>
                        )}
                    </div>
                    <div>
                        <h3 className={styles.footerTitle}>Преимущества работы в БКС</h3>
                        <ul className={styles.footerList}>{advantagesList}</ul>
                    </div>
                </div>
                <div className={styles.form}>
                    <VacancyApplyForm />
                </div>
            </div>
            {!isTablet && (
                <div className={styles.buttonList}>
                    <Button
                        className={styles.respond}
                        isDisabled={isLoadingVacancy}
                        as="button"
                        theme="blue"
                        onClick={handleButtonClick}
                        size="s"
                    >
                        Откликнуться
                    </Button>

                    <div className={styles.buttons}>
                        <CopyToClipboard
                            copiedValue={window.location.href}
                            copiedMessage="Ссылка на вакансию скопированна"
                            duration={2000}
                        >
                            <div className={styles.buttons__copy}>
                                <IconButton className={styles.copy} icon="copy" />
                                <p> Скопировать ссылку</p>
                            </div>
                        </CopyToClipboard>
                    </div>
                </div>
            )}
        </Container>
    );
};
